import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'

export default function ApplicationFunnelsPage() {
  const breadcrumbs = generateBreadcrumbs('/services/application-funnels', breadcrumbLabels)

  useSEO({
    title: 'Festival Application & Enrollment Funnels',
    description:
      'Automated application funnels that track every step from inquiry to enrollment. Stop losing applicants to manual chaos.',
    url: '/services/application-funnels',
    canonical: '/services/application-funnels',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Festival Application & Enrollment Funnels',
      description:
        'Automated application systems for classical music festivals with CRM integration, payment processing, and analytics.',
      url: '/services/application-funnels',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useScrollReveal<HTMLDivElement>('.problem-item', { stagger: 0.08 })
  const packageRef = useScrollReveal<HTMLDivElement>('.package-content')
  const stepsRef = useScrollReveal<HTMLDivElement>('.step-card', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Stop Losing Applicants to Manual Chaos. Start Tracking Every Enrollment Step.</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your festival has 127 inquiries. 82 started applications. But only 34 enrolled. Where did everyone else go?
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Most festivals have no idea. They track inquiries in one spreadsheet, applications in another, and payments in
          a third. Students fall through the cracks. Staff drowns in admin work.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          There&apos;s a better way. We build automated application funnels that track every step from inquiry to
          enrollment—so you finally know what&apos;s working and what&apos;s broken.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Does This Sound Like Your Application Season?</h2>
        <div ref={problemsRef} className="space-y-6">
          {[
            {
              problem: 'No Visibility Into Your Funnel',
              description:
                "You have no idea how many people inquired, how many started applications, how many submitted, how many paid. You're flying blind.",
            },
            {
              problem: 'Manual Tracking Across 5 Different Tools',
              description:
                'Inquiries in Gmail. Applications in Google Forms. Payments in Stripe. Spreadsheets everywhere. Nothing talks to each other.',
            },
            {
              problem: 'Applicants Falling Through the Cracks',
              description:
                "Someone inquired in January but never applied. Did you follow up? Did they lose interest? Did your email go to spam? You'll never know.",
            },
            {
              problem: 'Hours of Manual Data Entry Every Week',
              description:
                "Your staff spends 10-20 hours per week copying data from one system to another, sending follow-up emails manually, and trying to keep track of who's who.",
            },
            {
              problem: 'Confusing Application Process',
              description:
                'Students have to fill out a form, then email documents, then pay via a separate link, then confirm via another email. Half of them give up before finishing.',
            },
            {
              problem: 'No Automated Follow-Ups',
              description:
                "When someone inquires, you manually send a follow-up. When they apply, you manually send a confirmation. When they don't pay, you manually remind them. It's exhausting.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-item border-destructive bg-destructive/5 space-y-2 border-l-4 py-4 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-6 text-center text-lg italic">
          The worst part? You have no data to improve next year. You don&apos;t know which marketing channels drive
          applications, which email sequences convert, or where applicants drop off. So you repeat the same chaos every
          season.
        </p>
      </section>

      {/* Solution Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">The Solution: An Automated Application & Enrollment Funnel</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Imagine this: Someone visits your website. They submit an inquiry form. Immediately, they get a welcome
            email. Three days later, they get a follow-up with application details. They start the application, and you
            see exactly where they are in the process. They submit, and you get notified. They pay, and it&apos;s
            automatically recorded. All in one system. No spreadsheets. No manual follow-ups. No lost applicants.
          </p>
        </div>

        <div ref={packageRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-8">
          <div className="package-content space-y-6">
            <h3 className="text-center text-xl font-semibold">
              Application & Enrollment Funnel Package: $8,000-$15,000
            </h3>
            <p className="text-muted-foreground text-center">
              A complete system that tracks every step from inquiry to enrollment—so you finally see who&apos;s
              applying, who&apos;s enrolling, and what&apos;s actually working.
            </p>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold">What&apos;s Included:</h4>
              <ul className="space-y-3">
                {[
                  'Complete application funnel: inquiry form → application submission → payment → enrollment confirmation',
                  'Automated email sequences at every step (welcome emails, follow-ups, reminders, confirmations)',
                  'CRM integration to track every applicant (Airtable, HubSpot, Mailchimp, or custom solution)',
                  'Payment processing integration (Stripe, PayPal, or your preferred system)',
                  'Admin dashboard where you can see every inquiry, application, and enrollment in real-time',
                  'Application status tracking: who inquired, who started, who submitted, who paid, who enrolled',
                  'Automated follow-ups for incomplete applications ("You started your application but haven\'t submitted—need help?")',
                  "Analytics and reporting: conversion rates at every funnel step so you know what's working",
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-border space-y-4 border-t pt-6">
              <h4 className="text-lg font-semibold">What You&apos;ll Finally Know:</h4>
              <ul className="text-muted-foreground space-y-2 text-sm">
                <li>- How many people inquired vs. how many actually applied (conversion rate)</li>
                <li>- How many started applications vs. how many submitted (completion rate)</li>
                <li>- How many submitted vs. how many paid (enrollment rate)</li>
                <li>- Which email campaigns drive the most applications</li>
                <li>- Where applicants drop off in your funnel (so you can fix it)</li>
                <li>- Exact data you need to improve enrollment year over year (no more guessing)</li>
              </ul>
            </div>

            <div className="border-border space-y-2 border-t pt-6">
              <p className="font-semibold">Timeline: 6-10 weeks from kickoff to launch</p>
              <p className="text-muted-foreground text-sm">
                Week 1-2: Discovery & Funnel Design
                <br />
                Week 3-6: Development & Integrations (forms, email, CRM, payment)
                <br />
                Week 7-9: Testing & Content Setup
                <br />
                Week 10: Launch & Training
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">How Your New Funnel Will Work</h2>
        <div ref={stepsRef} className="space-y-6">
          {[
            {
              step: 'Step 1: Inquiry',
              description:
                'A prospective student visits your website and fills out an inquiry form (name, email, instrument, program interest).',
              automation:
                '→ Automated welcome email sent immediately. → Contact added to CRM with "Inquiry" status. → Automated follow-up email sent 3 days later with application details.',
            },
            {
              step: 'Step 2: Application Start',
              description: 'Student clicks "Apply Now" in your email and starts filling out the application.',
              automation:
                '→ CRM status updated to "Application Started." → You can see their progress in your admin dashboard. → If they don\'t submit within 7 days, automated reminder email sent.',
            },
            {
              step: 'Step 3: Application Submission',
              description:
                'Student completes and submits the application (including uploaded documents, recommendations, etc.).',
              automation:
                '→ CRM status updated to "Application Submitted." → Automated confirmation email sent to student. → Notification email sent to your team. → Application data automatically recorded in your system.',
            },
            {
              step: 'Step 4: Payment',
              description:
                'Student receives payment instructions and submits tuition/deposit via integrated payment system.',
              automation:
                '→ Payment automatically processed via Stripe/PayPal. → CRM status updated to "Enrolled." → Automated enrollment confirmation email sent with next steps. → Payment data synced to your records.',
            },
            {
              step: 'Step 5: Enrollment Confirmation',
              description: 'Student is officially enrolled. Your team can see all enrollment data in one place.',
              automation:
                '→ Final confirmation email sent with program details, schedule, what to bring, etc. → Student added to enrolled students list for upcoming session.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="step-card border-border space-y-3 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  className="bg-primary text-primary-foreground flex h-10 w-10 items-center justify-center rounded-full font-bold"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {idx + 1}
                </motion.div>
                <h3 className="text-lg font-semibold">{item.step}</h3>
              </div>
              <p className="text-muted-foreground pl-13">{item.description}</p>
              <div className="bg-primary/5 border-primary border-l-4 py-3 pl-4 text-sm">
                <p className="text-primary mb-2 font-semibold">What Happens Automatically:</p>
                <p className="text-muted-foreground whitespace-pre-line">{item.automation}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What This Means for Your Festival</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Reclaim 10-20 Hours Per Week',
              description:
                "No more manual follow-ups, no more copying data between spreadsheets, no more chasing down incomplete applications. It's all automated.",
            },
            {
              benefit: 'Never Lose an Applicant Again',
              description:
                'Every inquiry is tracked. Every incomplete application gets a reminder. Every step is logged. No one falls through the cracks.',
            },
            {
              benefit: "See Exactly What's Working",
              description:
                'Track conversion rates at every step. Know which marketing channels drive applications. Optimize your funnel year over year.',
            },
            {
              benefit: 'Reduce Enrollment Friction',
              description:
                'Students can inquire, apply, and pay in one seamless process. No jumping between 5 different tools. Higher completion rates.',
            },
            {
              benefit: 'Professional Applicant Experience',
              description:
                'Automated emails at every step make your festival look organized and professional. Students feel supported throughout the process.',
            },
            {
              benefit: 'Data to Improve Next Year',
              description:
                "After your season, you'll have exact data on inquiries, applications, drop-off points, and enrollments. Use it to improve next year.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can this integrate with our existing website?',
              answer:
                'Yes. We can integrate the funnel with your current website, or we can build a new festival website as part of our Festival Website Package and include the funnel.',
            },
            {
              question: 'What CRM do you recommend?',
              answer:
                'It depends on your needs and budget. For most festivals, we recommend Airtable (affordable, flexible, easy to use) or HubSpot (more robust, free tier available). We can also build a custom CRM if you have specific requirements.',
            },
            {
              question: 'Can applicants upload documents (recommendations, recordings, etc.)?',
              answer:
                'Absolutely. We can set up document uploads for recommendations, repertoire lists, recordings, resumes—whatever your application requires.',
            },
            {
              question: 'What payment systems do you integrate with?',
              answer:
                'Stripe (most common), PayPal, Square, or custom solutions. We can also integrate with existing payment processors if you already have one.',
            },
            {
              question: 'How do we handle application reviews and acceptances?',
              answer:
                'We build an admin dashboard where you can review applications, add notes, change statuses (under review, accepted, waitlisted, declined), and send acceptance/rejection emails—all from one place.',
            },
            {
              question: 'What if our application process is complex (auditions, multiple rounds, etc.)?',
              answer:
                "We can handle complex workflows: multi-step applications, audition scheduling, jury scoring, waitlists, conditional offers, etc. We'll design the funnel to match your exact process.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Streamline Your Application Process?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute funnel audit. We&apos;ll review your current application process, identify where
            applicants are dropping off, and show you exactly how an automated funnel would work for your festival.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No obligation. Just honest feedback on your application process and clear next steps if you want our help.
          </p>
        </div>
      </section>
    </div>
  )
}
